import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Switch,
  CircularProgress,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from '@mui/material';
import { useAppDispatch } from 'store/hooks';
import { showToast } from 'store/toastSlice';
import { siteWheelService } from 'components/api/siteWheel';
import styles from '../userDetails.module.scss';

interface Props {
  userId: string;
}

const DailyWheelSettings: React.FC<Props> = ({ userId }) => {
  const dispatch = useAppDispatch();
  const siteId = 1;

  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({
    isEnabled: false,
    maxAmount: '' as string,
    additionalSpinCount: '' as string,
  });

  const [currentSpins, setCurrentSpins] = useState<number>(0);
  const [hasChangesSection1, setHasChangesSection1] = useState(false);
  const [hasChangesSection2, setHasChangesSection2] = useState(false);

  // Modal State
  const [modalOpen, setModalOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [isAddingSpins, setIsAddingSpins] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, [userId]);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await siteWheelService.getUserSettings(userId, siteId);
      const data = res.data || res;
      setSettings({
        isEnabled: !!data.isEnabled,
        maxAmount:
          data.maxAmount !== null && data.maxAmount !== undefined ? String(data.maxAmount) : '',
        additionalSpinCount: '',
      });
      setCurrentSpins(Number(data.additionalSpinCount) || 0);
      setHasChangesSection1(false);
      setHasChangesSection2(false);
    } catch (error) {
      dispatch(showToast({ text: 'Failed to fetch wheel settings', type: 'error' }));
    } finally {
      setLoading(false);
    }
  };

  const handleToggleEnable = async (checked: boolean) => {
    // Optimistic update
    setSettings((prev) => ({ ...prev, isEnabled: checked }));
    try {
      await siteWheelService.updateUserSettings(userId, siteId, {
        isEnabled: checked,
      });
      dispatch(showToast({ text: 'Wheel status updated successfully', type: 'success' }));
    } catch (error: any) {
      // Revert on failure
      setSettings((prev) => ({ ...prev, isEnabled: !checked }));
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Failed to update wheel status';
      dispatch(showToast({ text: errorMessage, type: 'error' }));
    }
  };

  const handleSaveSection1 = async () => {
    try {
      await siteWheelService.updateUserSettings(userId, siteId, {
        maxAmount: settings.maxAmount === '' ? null : Number(settings.maxAmount),
      });
      dispatch(showToast({ text: 'Max amount saved successfully', type: 'success' }));
      setHasChangesSection1(false);
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Failed to save max amount';
      dispatch(showToast({ text: errorMessage, type: 'error' }));
    }
  };

  const handleConfirmAddSpins = async () => {
    if (!reason.trim()) {
      dispatch(showToast({ text: 'Reason is required', type: 'error' }));
      return;
    }

    setModalOpen(false);
    setIsAddingSpins(true);
    try {
      await siteWheelService.addUserSettingsSpins(
        userId,
        siteId,
        Number(settings.additionalSpinCount),
        reason,
      );
      dispatch(showToast({ text: 'Spins added successfully', type: 'success' }));
      setCurrentSpins((prev) => prev + Number(settings.additionalSpinCount));
      setSettings((prev) => ({ ...prev, additionalSpinCount: '' }));
      setHasChangesSection2(false);
      setReason('');
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Failed to add spins';
      dispatch(showToast({ text: errorMessage, type: 'error' }));
    } finally {
      setIsAddingSpins(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress sx={{ color: 'var(--accent-blue)' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {/* Section 1: General Settings */}
      <Box
        sx={{
          backgroundColor: 'var(--bg-panel)',
          p: 3,
          borderRadius: '8px',
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
        }}
      >
        <Typography
          variant="h6"
          sx={{ color: 'var(--text-primary)', fontWeight: 600, textAlign: 'left' }}
        >
          General Settings
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'flex-start' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ color: 'var(--text-secondary)', textAlign: 'left' }}>
              {settings.isEnabled
                ? 'Daily wheel is active for this user'
                : 'Daily wheel is disabled for this user'}
            </Typography>
            <Switch
              checked={settings.isEnabled}
              onChange={(e) => handleToggleEnable(e.target.checked)}
              color="primary"
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography
                variant="caption"
                sx={{ color: 'var(--text-secondary)', textAlign: 'left' }}
              >
                Max Amount
              </Typography>
              <input
                className={styles.inputField}
                placeholder="Unlimited"
                value={settings.maxAmount}
                onChange={(e) => {
                  const val = e.target.value.replace(/[^0-9]/g, '');
                  setSettings({ ...settings, maxAmount: val });
                  setHasChangesSection1(true);
                }}
                style={{ width: '150px' }}
              />
            </Box>
            <Button
              variant="contained"
              disabled={!hasChangesSection1}
              onClick={handleSaveSection1}
              sx={{
                height: '32px',
                fontSize: '13px',
                padding: '4px 16px',
                textTransform: 'none',
                backgroundColor: '#4caf50',
                color: '#fff',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                '&:hover': {
                  backgroundColor: '#43a047',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
                },
                '&.Mui-disabled': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  color: 'rgba(255, 255, 255, 0.3)',
                },
              }}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Box>

      <Divider sx={{ borderColor: 'var(--border-color, rgba(255, 255, 255, 0.1))' }} />

      {/* Section 2: Additional Spins */}
      <Box
        sx={{
          backgroundColor: 'var(--bg-panel)',
          p: 3,
          borderRadius: '8px',
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography
            variant="h6"
            sx={{ color: 'var(--text-primary)', fontWeight: 600, textAlign: 'left' }}
          >
            Additional Spins
          </Typography>
          <Typography variant="body2" sx={{ color: 'var(--text-secondary)', textAlign: 'left' }}>
            User currently has{' '}
            <Box component="span" sx={{ color: 'var(--accent-green)', fontWeight: 600 }}>
              {currentSpins}
            </Box>{' '}
            unused additional spin{currentSpins !== 1 ? 's' : ''}.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography
              variant="caption"
              sx={{ color: 'var(--text-secondary)', textAlign: 'left' }}
            >
              Additional Spin Count
            </Typography>
            <input
              className={styles.inputField}
              placeholder="0"
              value={settings.additionalSpinCount}
              onChange={(e) => {
                const val = e.target.value.replace(/[^0-9]/g, '');
                setSettings({ ...settings, additionalSpinCount: val });
                setHasChangesSection2(true);
              }}
              style={{ width: '150px' }}
            />
          </Box>
          <Button
            variant="contained"
            disabled={
              !hasChangesSection2 ||
              settings.additionalSpinCount === '' ||
              Number(settings.additionalSpinCount) <= 0 ||
              isAddingSpins
            }
            onClick={() => setModalOpen(true)}
            sx={{
              height: '32px',
              fontSize: '13px',
              padding: '4px 16px',
              textTransform: 'none',
              backgroundColor: '#4caf50',
              color: '#fff',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
              '&:hover': {
                backgroundColor: '#43a047',
                boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
              },
              '&.Mui-disabled': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: 'rgba(255, 255, 255, 0.3)',
              },
            }}
          >
            Give spins
          </Button>
        </Box>
      </Box>

      {/* Reason Modal */}
      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: 'var(--bg-panel)',
            color: 'var(--text-primary)',
            minWidth: '350px',
          },
        }}
      >
        <DialogTitle>Reason for extra spins</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Reason"
            fullWidth
            variant="outlined"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            sx={{
              marginTop: 1,
              '& .MuiOutlinedInput-root': {
                color: 'var(--text-primary)',
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.23)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                },
              },
              '& .MuiInputLabel-root': {
                color: 'var(--text-secondary)',
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => setModalOpen(false)} sx={{ color: 'var(--text-secondary)' }}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAddSpins}
            variant="contained"
            disabled={!reason.trim()}
            sx={{
              backgroundColor: '#2a8039',
              '&:hover': { backgroundColor: '#2a8039' },
              '&.Mui-disabled': {
                backgroundColor: 'rgba(255, 255, 255, 0.12)',
              },
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DailyWheelSettings;
